import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const NevadaTemporaryPermitsGuidePage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <section className="pt-28 pb-12 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/nevada-dmv-info" className="hover:text-gold-400">DMV Info</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">Temporary Operating Permits</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-white mb-3">Nevada Temporary Operating Permits</h1>
        <p className="text-obsidian-300">When you need a temporary permit to legally operate a vehicle in Nevada before full registration.</p>
      </div>
    </section>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <EducationalDisclaimer />
      <div className="space-y-8 text-obsidian-700">
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">What Is a Temporary Operating Permit?</h2>
          <p>A Temporary Operating Permit (TOP) allows you to legally drive a vehicle on Nevada roads while you complete the full registration process. It is a short-term authorization — typically valid for <strong>30 days</strong> — that gives you time to gather required documents, pass emissions, or complete other requirements.</p>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">When You May Need a Temporary Permit</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { title: 'New Purchase', desc: 'Just bought a vehicle and need time to complete registration requirements.' },
              { title: 'New Resident', desc: 'Recently moved to Nevada and need time to get Nevada insurance and pass emissions.' },
              { title: 'Pending Emissions', desc: 'Vehicle needs repairs before it can pass the emissions test.' },
              { title: 'Missing Documents', desc: 'Waiting for a title, lien release, or other required document.' },
            ].map(item => (
              <div key={item.title} className="border border-obsidian-100 rounded-lg p-4">
                <p className="font-semibold text-obsidian-900 text-sm mb-1">{item.title}</p>
                <p className="text-obsidian-500 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Important Rules</h2>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <ul className="space-y-2 text-sm text-amber-800">
              {['Temporary permits are not renewable — you must complete full registration before expiration', 'The permit must be displayed in the vehicle at all times', 'You must still have valid Nevada insurance', 'Driving with an expired temporary permit can result in a citation', 'Temporary permits are issued by the DMV or authorized providers like Speedy DMV Services'].map(item => (
                <li key={item} className="flex items-start space-x-2"><i className="ri-information-line flex-shrink-0 mt-0.5"></i><span>{item}</span></li>
              ))}
            </ul>
          </div>
        </section>
        <div className="bg-gold-50 border border-gold-200 rounded-xl p-6 text-center">
          <h3 className="font-display font-bold text-obsidian-900 text-lg mb-2">Need a Temporary Permit?</h3>
          <p className="text-obsidian-600 text-sm mb-4">Speedy DMV Services can issue temporary operating permits and help you complete full registration.</p>
          <Link to="/appointment" className="inline-block bg-gold-gradient text-obsidian-900 px-6 py-2.5 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Book Appointment</Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default NevadaTemporaryPermitsGuidePage;
